import { DialogCloseEvent } from '@theme/dialog';
import { clamp } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 0.25;
const FIT_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const ZOOM_STEP = 0.25;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = FIT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {Map<number, Point>} */
  #pointerPositions = new Map();

  /** @type {{distance: number, scale: number, midpoint: Point, translate: Point} | null} */
  #pinchStart = null;

  /** @type {number | null} */
  #pointerId = null;

  /** @type {boolean} */
  #suppressClick = false;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#resizeObserver.observe(this);
    const { signal } = this.#controller;
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => this.#requestUpdateTransform();

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Tracks mouse, pen, and touch pointers with one gesture path at every viewport size.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const isTouch = event.pointerType === 'touch';
    this.#suppressClick = isTouch;
    if (isTouch) event.preventDefault();
    this.#pointerPositions.set(event.pointerId, { x: event.clientX, y: event.clientY });

    try {
      this.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Pointer capture is not available in every embedded browser surface.
    }

    if (this.#pointerPositions.size >= 2) {
      event.preventDefault();
      this.#startPinchGesture();
      return;
    }

    if (isTouch && this.#handleTouchDoubleTap(event)) return;

    if (this.#scale > FIT_ZOOM) {
      event.preventDefault();
      this.#pointerId = event.pointerId;
      this.#startDragGestureFromTouch(event);
      this.style.cursor = 'grabbing';
    }
  };

  /** @param {PointerEvent} touch */
  #handleTouchDoubleTap(touch) {
    const currentTime = performance.now();
    const isDoubleTap = this.#lastTapPosition
      && currentTime - this.#lastTapTime < DOUBLE_TAP_DELAY
      && getDistance(touch, this.#lastTapPosition) < DOUBLE_TAP_DISTANCE;

    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
    if (!isDoubleTap) return false;

    this.#handleDoubleTapFromTouch(touch);
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#suppressClick = true;
    return true;
  }

  /** @param {PointerEvent} touch */
  #handleDoubleTapFromTouch(touch) {
    const targetZoom = this.#scale > FIT_ZOOM + 0.05 ? FIT_ZOOM : DEFAULT_ZOOM;
    const oldScale = this.#scale;
    const rect = this.getBoundingClientRect();
    const distanceFromCenter = {
      x: touch.clientX - (rect.left + rect.width / 2),
      y: touch.clientY - (rect.top + rect.height / 2),
    };

    if (targetZoom === FIT_ZOOM) {
      this.#scale = FIT_ZOOM;
      this.#translate = { x: 0, y: 0 };
    } else {
      this.#scale = Math.min(MAX_ZOOM, targetZoom);
      const scaleDelta = this.#scale / oldScale - 1;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    }

    this.#requestUpdateTransform();
  }

  #startPinchGesture() {
    const [first, second] = Array.from(this.#pointerPositions.values());
    if (!first || !second) return;

    // A multi-pointer gesture is not a pair of taps.
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;

    const distance = getDistance(first, second);
    if (!distance) return;

    this.#pinchStart = {
      distance,
      scale: this.#scale,
      midpoint: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
      translate: { ...this.#translate },
    };
    this.#pointerId = null;
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
  }

  #processPinchGesture() {
    if (!this.#pinchStart || this.#pointerPositions.size < 2) return;
    const [first, second] = Array.from(this.#pointerPositions.values());
    if (!first || !second) return;

    const currentDistance = getDistance(first, second);
    const currentMidpoint = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
    const nextScale = clamp((currentDistance / this.#pinchStart.distance) * this.#pinchStart.scale, MIN_ZOOM, MAX_ZOOM);
    const rect = this.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    this.#translate = {
      x: (currentMidpoint.x - centerX) / nextScale
        - (this.#pinchStart.midpoint.x - centerX) / this.#pinchStart.scale
        + this.#pinchStart.translate.x,
      y: (currentMidpoint.y - centerY) / nextScale
        - (this.#pinchStart.midpoint.y - centerY) / this.#pinchStart.scale
        + this.#pinchStart.translate.y,
    };
    this.#scale = nextScale;
    this.#hasDraggedBeyondThreshold = Math.abs(currentDistance - this.#pinchStart.distance) >= DRAG_THRESHOLD;
    this.#requestUpdateTransform();
  }

  /**
   * Start a one-pointer pan gesture.
   * @param {Touch | PointerEvent | Point} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = {
      x: touch.clientX ?? touch.x,
      y: touch.clientY ?? touch.y,
    };
    this.#startTranslate = { ...this.#translate };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Process drag gesture from touch or pointer input.
   * @param {Touch | PointerEvent} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#pointerPositions.has(event.pointerId)) return;
    this.#pointerPositions.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointerPositions.size >= 2) {
      event.preventDefault();
      this.#processPinchGesture();
      return;
    }

    if (event.pointerId === this.#pointerId && this.#isDragging) {
      event.preventDefault();
      this.#processDragGesture(event);
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#pointerPositions.has(event.pointerId)) return;

    const gestureChangedImage = this.#hasDraggedBeyondThreshold || Boolean(this.#pinchStart);
    if (gestureChangedImage) this.#suppressClick = true;
    if (event.type === 'pointercancel') this.#suppressClick = false;
    this.#pointerPositions.delete(event.pointerId);

    if (this.#pointerPositions.size < 2) this.#pinchStart = null;

    if (this.#pointerPositions.size === 1 && this.#scale > FIT_ZOOM) {
      const [[pointerId, position]] = this.#pointerPositions;
      this.#pointerId = pointerId;
      this.#startDragGestureFromTouch(position);
      this.style.cursor = 'grabbing';
    } else if (this.#pointerPositions.size === 0) {
      this.#pointerId = null;
      this.#isDragging = false;
      this.#hasDraggedBeyondThreshold = false;
      this.style.cursor = this.#scale > FIT_ZOOM ? 'grab' : '';
    }

    this.#requestUpdateTransform();
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between 25% of fit and 5x fit.
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At or below the fit-to-view size, the full image is visible with no dragging allowed.
    if (this.#scale <= FIT_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Get wrapper dimensions
    const wrapperRect = this.getBoundingClientRect();

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.style.cursor = this.#scale > FIT_ZOOM ? 'grab' : '';
    this.dispatchEvent(new CustomEvent('dragzoom:change', { bubbles: true, detail: { scale: this.#scale } }));
  };

  /**
   * Reset zoom to the full-fit state, centered in the viewer.
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    this.#scale = FIT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#suppressClick = false;
    this.#pointerId = null;
    this.#pointerPositions.clear();
    this.#pinchStart = null;
    this.#updateTransform();
  };

  /** @returns {number} Current zoom scale. */
  getZoom() {
    return this.#scale;
  }

  /** @param {number} nextZoom Desired scale. @returns {number} Applied scale. */
  setZoom(nextZoom) {
    this.#scale = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= FIT_ZOOM) this.#translate = { x: 0, y: 0 };

    this.#requestUpdateTransform();
    return this.#scale;
  }

  /** @returns {number} Applied scale. */
  zoomIn() {
    return this.setZoom(this.#scale + ZOOM_STEP);
  }

  /** @returns {number} Applied scale. */
  zoomOut() {
    return this.setZoom(this.#scale - ZOOM_STEP);
  }

  resetZoom() {
    this.#resetZoom();
  }

  /** Consume the click generated after a drag so it does not close the viewer. */
  consumeClick() {
    const shouldSuppress = this.#suppressClick;
    this.#suppressClick = false;
    return shouldSuppress;
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Pick<Touch, 'clientX' | 'clientY'>} point1 - First point or touch
 * @param {Point | Pick<Touch, 'clientX' | 'clientY'>} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Pick<Touch, 'clientX' | 'clientY'>} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Pick<Touch, 'clientX' | 'clientY'>} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Pick<Touch, 'clientX' | 'clientY'>} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Pick<Touch, 'clientX' | 'clientY'>} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
